import { shownEveningVariantId } from '@/lib/eveningClient';
import { logCtaClick } from '@/lib/ctaTracking';
import { getSessionId } from '@/lib/trackerSession'; // one tracker id, created if missing (lead ↔ visit, Founder 2026-09-27)
// Client-side helper for logging a company-WhatsApp inquiry — see app/api/inquiries/route.ts
// for what actually gets written (he_inquiries). Used by every new lead-gen surface that
// routes through 972506403800: FloatingWaButton, QuickConsultWidget, the article
// hook-questionnaire CTA. Fire-and-forget by design — never blocks opening WhatsApp.
export const INQUIRY_SOURCES = ['floating_button', 'quick_consult', 'hook_questionnaire', 'manual_whatsapp', 'consult'] as const;
export type InquirySource = typeof INQUIRY_SOURCES[number];

export type LogInquiryArgs = {
  source: InquirySource;
  articleId?: number;
  categorySlug?: string;
  city?: string;
  cityLat?: number;
  cityLng?: number;
  questionnaireAnswered?: boolean;
  questionnaireAnswers?: Record<string, unknown>;
  ctaVariantId?: number;
  eveningVariantId?: number;
  messageText?: string;
  proId?: number; // NEW FLOW (2026-09-25): the specific professional the visitor asked for
};

function getSessionIds(): { sessionId: string | null; trackerSessionId: string | null } {
  let trackerSessionId: string | null = null;
  try { trackerSessionId = getSessionId(); } catch {}
  return { sessionId: null, trackerSessionId };
}

/** Logs the inquiry and resolves with its ticket number (e.g. "13501"; legacy rows "INQ-000123"), or null on
 *  failure — callers should still proceed to open WhatsApp either way (see companyWa.ts). */
export async function logInquiry(args: LogInquiryArgs): Promise<string | null> {
  try {
    const { sessionId, trackerSessionId } = getSessionIds();
    // article attribution (14/10/26): a pro-card / any inquiry fired on an article page carries the article even when the caller
    // (CategoryProCard -> contactViaCompany) does not know it - ArticleView stamps data-article-id on its root.
    let articleId = args.articleId;
    if (articleId == null && typeof document !== 'undefined') {
      const n = Number(document.querySelector('[data-article-id]')?.getAttribute('data-article-id'));
      if (n > 0) articleId = n;
    }
    // evening reassurance note (Founder 14/10/26): the variant on screen when the visitor tapped -> the inquiry row + a CTA click event
    const eveningVariantId = args.eveningVariantId ?? shownEveningVariantId();
    if (eveningVariantId) logCtaClick(eveningVariantId, articleId);
    const res = await fetch('/api/inquiries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        ...args,
        eveningVariantId,
        articleId,
        sessionId,
        trackerSessionId,
        pagePath: typeof location !== 'undefined' ? location.pathname + location.search : null,
      }),
    });
    const data = await res.json();
    return data.ok ? data.ticketNo : null;
  } catch {
    return null;
  }
}
